import { ApolloQueryResult } from '@apollo/client';
import { useNavigate, useParams } from '@hulu/react-router-dom';
import { useContext, useMemo, useState } from 'react';

import useDownloadPerformanceReport from '../../apis/graphql/reports/useDownloadPerformanceReport';
import {
  EntityType,
  GetCampaignsQuery,
  GetLineItemsByCampaignIdQuery,
  LineItemByIdQuery,
} from '../../apis/graphql/types';
import { GlobalPermissionsContext } from '../../context';
import { GhostModeContext } from '../../context/GhostMode/GhostMode';
import useHandleQuickActions from '../../hooks/quickActions/useHandleQuickActions';
import useUserPermissions from '../../hooks/useUserPermissions';
import { ConfirmModalWrapper } from '../../pages/AccountManagement/Brands/AdAccountDetails/DeleteAdAccount/styled';
import { CampaignsTable } from '../../pages/Campaigns/CampaignsTable/constants';
import { NumLineItemsContext } from '../../pages/Campaigns/context';
import { MAX_LINE_ITEMS_COUNT } from '../../pages/Campaigns/Form/Navigation/constants';
import {
  getItemTypeName,
  getPluralEnding,
  isCancellable,
  isDeletable,
  isDownloadable,
  isEditable,
  isEnabledToResume,
  isPausable,
  isResumable,
} from '../../pages/Campaigns/utils';
import IconComponent, { getActionIcon } from '../IconComponent';
import Loader, { LoaderType } from '../Loader';
import { PerformanceCharsTitleDownloadContainer } from './styled';

export type Props = {
  campaignId: string;
  isCampaign?: boolean;
  isReview?: boolean;
  itemId: string;
  statusList: (string | null | undefined)[];
  adStatusList: (string | null | undefined)[];
  lineItems?: CampaignsTable['subRows'];
  isDisableDelete?: boolean;
  isDuplicateNoAllowed: boolean;
  refetch?: () => Promise<ApolloQueryResult<GetCampaignsQuery | GetLineItemsByCampaignIdQuery | LineItemByIdQuery>>;
  isKerv: boolean;
};

const QuickActions = ({
  campaignId,
  isCampaign = false,
  isReview,
  itemId,
  lineItems,
  statusList,
  adStatusList,
  isDisableDelete,
  isDuplicateNoAllowed,
  refetch,
  isKerv,
}: Props): JSX.Element => {
  const { isGhostMode } = useContext(GhostModeContext);
  const navigate = useNavigate();
  const { adAccountId } = useParams();
  const [modalShow, setModalShow] = useState({
    delete: false,
    cancel: false,
    pause: false,
    resume: false,
  });

  // Only admins in ghost mode can cancel or pause line items reserved for KERV.
  const isKervActionsDisabled = isKerv && !isGhostMode;

  const {
    quickActionsLoading,
    duplicateLoading,
    deleteActionLoading,
    handleCancelQuickAction,
    handleDeleteQuickAction,
    handleDuplicateQuickAction,
    handlePauseQuickAction,
    handleResumeQuickAction,
  } = useHandleQuickActions();
  const { getAllPerformance } = useDownloadPerformanceReport({
    lineItems: (lineItems?.map((lineItem) => lineItem.id) as string[]) || [itemId],
    campaignId,
  });

  const { entityTypes } = useUserPermissions();
  const { permissions, adAccountPermissions, roles } = useContext(GlobalPermissionsContext);

  const {
    RESUME_LINE_ITEM,
    DELETE_CAMPAIGN,
    UPDATE_CAMPAIGN,
    UPDATE_LINE_ITEM,
    CREATE_CAMPAIGN,
    CLONE_LINE_ITEM,
    PAUSE_LINE_ITEM,
    CANCEL_LINE_ITEM,
    DELETE_LINE_ITEM,
  } = roles?.includes('ORG_ADMIN') || !adAccountPermissions ? permissions : adAccountPermissions[adAccountId];
  const isMarketerOrg = useMemo(() => entityTypes.includes(EntityType.MarketerOrganization), [entityTypes]);

  const handleEditAction = (): void => {
    if (isReview) {
      navigate(`../lineItem/${itemId}`);
    } else {
      navigate(!isCampaign ? `${campaignId}/lineItem/${itemId}` : `${itemId}`);
    }
  };

  const pluralEnding = getPluralEnding(isCampaign);
  const itemTypeName = getItemTypeName(isCampaign);

  const { numLineItemsMap } = useContext(NumLineItemsContext);
  const totalLineItemsCount = numLineItemsMap.get(campaignId);

  const handleDeleteAction = async (): Promise<void> => {
    await handleDeleteQuickAction({ itemId, callback: refetch, isCampaign });

    setModalShow({ ...modalShow, delete: false });
  };

  const handleDuplicateAction = async (): Promise<void> => {
    await handleDuplicateQuickAction({ itemId, callback: refetch, isCampaign });
  };

  const handlePauseAction = async (): Promise<void> => {
    await handlePauseQuickAction({ itemId, callback: refetch, isCampaign, lineItems });

    setModalShow({ ...modalShow, pause: false });
  };

  const handleCancelAction = async (): Promise<void> => {
    await handleCancelQuickAction({ itemId, callback: refetch, isCampaign, lineItems });

    setModalShow({ ...modalShow, cancel: false });
  };

  const handleResumeAction = async (): Promise<void> => {
    await handleResumeQuickAction({ itemId, callback: refetch, isCampaign, lineItems });

    setModalShow({ ...modalShow, resume: false });
  };

  const deleteDisabledTooltip = isCampaign
    ? 'Campaign has been edited less than 5 min ago'
    : 'Line Item has been edited less than 5 min ago';

  const deleteIconDisabled = { name: 'Remove', tooltip: deleteDisabledTooltip };

  const editDisabled = isCampaign ? !UPDATE_CAMPAIGN : !UPDATE_LINE_ITEM;
  const lineItemsLimitReached = !!totalLineItemsCount && totalLineItemsCount >= MAX_LINE_ITEMS_COUNT;
  const duplicateDisabled = isCampaign
    ? !CREATE_CAMPAIGN && !CLONE_LINE_ITEM
    : !CLONE_LINE_ITEM || lineItemsLimitReached;
  const pauseDisabled = !PAUSE_LINE_ITEM;
  const cancelDisabled = !CANCEL_LINE_ITEM;
  const resumeDisabled = !isEnabledToResume(adStatusList) || !RESUME_LINE_ITEM;
  const deleteDisabled = isCampaign ? !DELETE_CAMPAIGN : !DELETE_LINE_ITEM;

  return (
    <>
      {duplicateLoading || deleteActionLoading || quickActionsLoading ? (
        <Loader type={LoaderType.transparent} />
      ) : (
        <>
          {!isDuplicateNoAllowed && (
            <IconComponent
              id={itemId}
              icon={getActionIcon('duplicate', isCampaign)}
              onClick={handleDuplicateAction}
              disabled={duplicateDisabled}
            />
          )}
          {isEditable({ isModificationAllowed: true, isCampaign, statusList }) && (
            <IconComponent
              id={itemId}
              icon={getActionIcon('edit', isCampaign)}
              onClick={handleEditAction}
              disabled={editDisabled}
            />
          )}
          {isPausable({ isModificationAllowed: true, statusList }) && (
            <IconComponent
              id={itemId}
              disabled={pauseDisabled || isKervActionsDisabled}
              icon={getActionIcon('pause', isCampaign)}
              onClick={(): void => {
                !isKervActionsDisabled && setModalShow({ ...modalShow, pause: true });
              }}
            />
          )}
          {isCancellable({ isModificationAllowed: true, statusList, isMarketerOrg }) && (
            <IconComponent
              id={itemId}
              disabled={cancelDisabled || isKervActionsDisabled}
              icon={getActionIcon('cancel', isCampaign)}
              onClick={(): void => {
                !isKervActionsDisabled && setModalShow({ ...modalShow, cancel: true });
              }}
            />
          )}
          {isResumable({ isModificationAllowed: true, statusList, isMarketerOrg }) && (
            <IconComponent
              id={itemId}
              icon={getActionIcon(!isEnabledToResume(adStatusList) ? 'disabledResume' : 'resume', isCampaign)}
              onClick={(): void => setModalShow({ ...modalShow, resume: true })}
              isCampaign={isCampaign}
              disabled={resumeDisabled}
            />
          )}
          {isDeletable({ isModificationAllowed: true, statusList }) && (
            <IconComponent
              id={itemId}
              icon={isDisableDelete ? deleteIconDisabled : getActionIcon('remove', isCampaign)}
              disabled={deleteDisabled}
              onClick={(): void => setModalShow({ ...modalShow, delete: true })}
            />
          )}
          {isDownloadable({ isModificationAllowed: true, statusList }) && (
            <PerformanceCharsTitleDownloadContainer
              data-testid="downloadAllReports"
              onClick={getAllPerformance}
              download
            >
              <IconComponent id={itemId} icon={getActionIcon('download', isCampaign)} />
            </PerformanceCharsTitleDownloadContainer>
          )}
        </>
      )}
      {modalShow.delete && (
        <ConfirmModalWrapper
          title={`Delete ${itemTypeName}`}
          text={
            isCampaign
              ? 'Do you want to delete this campaign? All line item drafts will be deleted. If all line items in the campaign are drafts, the campaign will also be deleted.'
              : 'Do you want to delete line item? If you delete a line item, Hulu will delete all information associated with this line item.'
          }
          visible={modalShow.delete}
          onConfirm={handleDeleteAction}
          onDeny={(): void => setModalShow({ ...modalShow, delete: false })}
          loading={deleteActionLoading}
        />
      )}
      {modalShow.cancel && (
        <ConfirmModalWrapper
          title={`Cancel Line Item${pluralEnding}`}
          text={`Do you want to cancel line item${pluralEnding}? Cancelling will stop line item${pluralEnding} delivery which cannot be resumed.`}
          visible={modalShow.cancel}
          onConfirm={handleCancelAction}
          onDeny={(): void => setModalShow({ ...modalShow, cancel: false })}
          loading={quickActionsLoading}
        />
      )}
      {modalShow.pause && (
        <ConfirmModalWrapper
          title={`Pause Line Item${pluralEnding}`}
          text={`Do you want to pause line item${pluralEnding}? Pausing will stop line item${pluralEnding} delivery. You can resume your line item${pluralEnding} at any time.`}
          visible={modalShow.pause}
          onConfirm={handlePauseAction}
          onDeny={(): void => setModalShow({ ...modalShow, pause: false })}
          loading={quickActionsLoading}
        />
      )}
      {modalShow.resume && (
        <ConfirmModalWrapper
          title={`Resume Line Item${pluralEnding}`}
          text={`Do you want to resume line item${pluralEnding}? Scheduled delivery will resume.`}
          visible={modalShow.resume}
          onConfirm={handleResumeAction}
          onDeny={(): void => setModalShow({ ...modalShow, resume: false })}
          loading={quickActionsLoading}
        />
      )}
    </>
  );
};

export default QuickActions;
